import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { KeyboardEvent, useEffect, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import routes from '../../config/routes';
import { useIsCurrentPath } from '../../hooks';

const idButton = 'header-options-button';
const idMenu = 'header-options-menu';
const menuListProps = {
  'aria-labelledby': idButton,
};

type ButtonSettingProps = { handleClose: () => void };

const ButtonSetting = ({ handleClose }: ButtonSettingProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showSettings = !useIsCurrentPath(routes.protectedRoutes.profile.path);
  const handleClick = () => {
    handleClose();
    navigate(routes.protectedRoutes.profile.path);
  };

  return showSettings ? (
    <MenuItem onClick={handleClick}>{t('layout.header.menu.settings')}</MenuItem>
  ) : null;
};

const MenuHeader = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick: IconButtonProps['onClick'] = event =>
    setAnchorEl(current => (current ? null : event.currentTarget));
  const handleClose = () => setAnchorEl(null);
  const handleListKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      handleClose();
    }
  };
  const handleClickLogout = () => {
    handleClose();
    instance.logoutRedirect();
  };

  useEffect(() => {
    handleClose();
  }, [location.pathname]);

  return (
    <>
      <IconButton
        id={idButton}
        data-tour="header-user-menu"
        aria-label={t('layout.header.aria.more')}
        aria-controls={open ? idMenu : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        color="inherit"
      >
        <MoreVertIcon />
      </IconButton>
      <Popper
        id={idMenu}
        anchorEl={anchorEl}
        open={open}
        placement="bottom-end"
        transition
        disablePortal
        sx={{ zIndex: theme => theme.zIndex.appBar + 2 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
            <Paper elevation={8} sx={{ mt: 1, borderRadius: 2, overflow: 'hidden' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} onKeyDown={handleListKeyDown} {...menuListProps}>
                  <ButtonSetting handleClose={handleClose} />
                  <MenuItem onClick={handleClickLogout}>{t('layout.header.menu.logout')}</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default MenuHeader;
